import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Building2, Users, CreditCard, Clock, CheckCircle, XCircle, AlertTriangle, Plus, Eye, Play, Pause, Calendar, RefreshCw, Key, Mail, DollarSign, Copy, ExternalLink, Sparkles, Terminal, FileText } from 'lucide-react';
import ClientCondominiumsReport from '@/components/ClientCondominiumsReport';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_STATUS = {
  TRIAL: { label: 'Trial', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  ACTIVE: { label: 'Attivo', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  EXPIRED: { label: 'Scaduto', color: 'bg-red-100 text-red-800', icon: XCircle },
  SUSPENDED: { label: 'Sospeso', color: 'bg-gray-100 text-gray-800', icon: AlertTriangle }
};

const PAYMENT_METHOD = {
  BANK_TRANSFER: 'Bonifico',
  CARD: 'Carta',
  CASH: 'Contanti',
  OTHER: 'Altro'
};

const PAYMENT_STATUS = {
  PENDING: { label: 'In attesa', color: 'bg-yellow-100 text-yellow-800' },
  PAID: { label: 'Pagato', color: 'bg-green-100 text-green-800' },
  FAILED: { label: 'Fallito', color: 'bg-red-100 text-red-800' },
  REFUNDED: { label: 'Rimborsato', color: 'bg-gray-100 text-gray-800' }
};

export const SuperAdmin = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [workspaces, setWorkspaces] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dev Tools state
  const [devConfig, setDevConfig] = useState(null);
  const [seedResult, setSeedResult] = useState(null);
  const [seedLoading, setSeedLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState('month');
  
  // Dialogs
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [isExtendOpen, setIsExtendOpen] = useState(false);
  const [isSuspendOpen, setIsSuspendOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isBillingOpen, setIsBillingOpen] = useState(false);
  
  // Form states
  const [createForm, setCreateForm] = useState({ name: '', admin_email: '', admin_full_name: '' });
  const [extendDays, setExtendDays] = useState(7);
  const [suspendReason, setSuspendReason] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    amount: '', method: 'BANK_TRANSFER', reference: '', notes: '', status: 'PAID'
  });
  const [billingForm, setBillingForm] = useState({
    legal_name: '', vat_number: '', fiscal_code: '', address: '', city: '',
    postal_code: '', email: '', email_pec: '', sdi_code: ''
  });

  // Check super admin access
  useEffect(() => {
    if (!user?.is_super_admin) {
      toast.error('Accesso non autorizzato');
      navigate('/');
    }
  }, [user, navigate]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setDashboard(await res.json());
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [token]);

  const fetchDevConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setDevConfig(await res.json());
    } catch (error) {
      console.error('Error fetching dev config:', error);
    }
  }, [token]);

  const fetchSubscriptionStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/subscription-stats?period=${statsPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setSubscriptionStats(await res.json());
    } catch (error) {
      console.error('Error fetching subscription stats:', error);
    }
  }, [token, statsPeriod]);

  const fetchWorkspaces = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('plan_status', statusFilter);
      
      const res = await fetch(`${API_URL}/api/superadmin/workspaces?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWorkspaces(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    }
    setLoading(false);
  }, [token, searchTerm, statusFilter]);

  useEffect(() => {
    if (user?.is_super_admin) {
      fetchDashboard();
      fetchWorkspaces();
      fetchDevConfig();
      fetchSubscriptionStats();
    }
  }, [user, fetchDashboard, fetchWorkspaces, fetchDevConfig, fetchSubscriptionStats]);

  // Run seed demo
  const handleRunSeed = async () => {
    setSeedLoading(true);
    setSeedResult(null);
    
    try {
      const res = await fetch(`${API_URL}/api/superadmin/dev/seed-demo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSeedResult(data);
        toast.success('Seed demo eseguito con successo!');
        fetchDashboard();
        fetchWorkspaces();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nel seed');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
    
    setSeedLoading(false);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiato!`);
  };

  const fetchWorkspaceDetail = async (workspaceId) => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/workspaces/${workspaceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedWorkspace(data);
        setBillingForm(data.billing_customer || {
          legal_name: '', vat_number: '', fiscal_code: '', address: '', city: '',
          postal_code: '', email: '', email_pec: '', sdi_code: ''
        });
        setIsDetailOpen(true);
      }
    } catch (error) {
      toast.error('Errore nel caricamento dettagli');
    }
  };

  const handleCreateWorkspace = async () => {
    if (!createForm.name || !createForm.admin_email || !createForm.admin_full_name) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/superadmin/workspaces`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Workspace creato! Password: ${data.temp_password}`);
        setIsCreateOpen(false);
        setCreateForm({ name: '', admin_email: '', admin_full_name: '' });
        fetchWorkspaces();
        fetchDashboard();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nella creazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleActivate = async (workspaceId) => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/workspaces/${workspaceId}/activate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Workspace attivato');
        fetchWorkspaces();
        if (selectedWorkspace) fetchWorkspaceDetail(workspaceId);
      }
    } catch (error) {
      toast.error('Errore nell\'attivazione');
    }
  };

  const handleActivateReconfirm = async (workspaceId) => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/workspaces/${workspaceId}/activate-reconfirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Attivato! Email di reset password inviata');
        if (data.reset_link) {
          console.log('Reset link (dev mode):', data.reset_link);
        }
        fetchWorkspaces();
        if (selectedWorkspace) fetchWorkspaceDetail(workspaceId);
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleSuspend = async () => {
    if (!suspendReason) {
      toast.error('Inserisci un motivo');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/superadmin/workspaces/${selectedWorkspace.id}/suspend`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: suspendReason })
      });
      
      if (res.ok) {
        toast.success('Workspace sospeso');
        setIsSuspendOpen(false);
        setSuspendReason('');
        fetchWorkspaces();
        fetchWorkspaceDetail(selectedWorkspace.id);
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleExpire = async (workspaceId) => {
    if (!confirm('Sei sicuro di voler forzare la scadenza?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/superadmin/workspaces/${workspaceId}/expire`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Workspace scaduto');
        fetchWorkspaces();
        if (selectedWorkspace) fetchWorkspaceDetail(workspaceId);
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleExtendTrial = async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/workspaces/${selectedWorkspace.id}/extend-trial`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ days: extendDays })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Trial estesa fino a ${formatDate(data.new_trial_ends_at)}`);
        setIsExtendOpen(false);
        fetchWorkspaces();
        fetchWorkspaceDetail(selectedWorkspace.id);
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount) {
      toast.error('Inserisci l\'importo');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/superadmin/workspaces/${selectedWorkspace.id}/payments`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...paymentForm,
          amount: parseFloat(paymentForm.amount)
        })
      });
      
      if (res.ok) {
        toast.success('Pagamento registrato');
        setIsPaymentOpen(false);
        setPaymentForm({ amount: '', method: 'BANK_TRANSFER', reference: '', notes: '', status: 'PAID' });
        fetchWorkspaceDetail(selectedWorkspace.id);
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleUpdateBilling = async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/workspaces/${selectedWorkspace.id}/billing`, {
        method: 'PATCH',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(billingForm)
      });
      
      if (res.ok) {
        toast.success('Dati fatturazione aggiornati');
        setIsBillingOpen(false);
        fetchWorkspaceDetail(selectedWorkspace.id);
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleForcePasswordReset = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/users/${userId}/force-password-reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Email reset password inviata');
        if (data.reset_link) {
          console.log('Reset link (dev mode):', data.reset_link);
        }
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (!user?.is_super_admin) return null;

  return (
    <div className="space-y-6 p-6">
      {/* Dashboard Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Workspace Totali</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.workspaces?.total || 0}</div>
            <div className="flex gap-2 mt-2 text-xs">
              <Badge className="bg-yellow-100 text-yellow-800">Trial: {dashboard?.workspaces?.by_status?.trial || 0}</Badge>
              <Badge className="bg-green-100 text-green-800">Attivi: {dashboard?.workspaces?.by_status?.active || 0}</Badge>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Utenti Totali</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.users?.total || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {dashboard?.users?.super_admins || 0} super admin
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Fatturato 30gg</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">€{dashboard?.revenue?.last_30_days?.toFixed(2) || '0.00'}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {dashboard?.revenue?.payments_count || 0} pagamenti
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Scadenza</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {dashboard?.expiring_soon?.length || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Nei prossimi 3 giorni
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-4">
        <Button 
          variant="outline" 
          onClick={() => navigate('/superadmin/fatture')}
          className="flex items-center gap-2"
        >
          <FileText className="h-4 w-4" />
          Fatture Elettroniche
        </Button>
      </div>

      {/* Subscription Stats Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              <CardTitle>Statistiche Abbonamenti</CardTitle>
            </div>
            <select
              value={statsPeriod}
              onChange={(e) => setStatsPeriod(e.target.value)}
              className="px-3 py-1.5 text-sm border rounded-md"
            >
              <option value="week">Ultima settimana</option>
              <option value="month">Ultimo mese</option>
              <option value="quarter">Ultimo trimestre</option>
              <option value="year">Ultimo anno</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {subscriptionStats ? (
            <div className="space-y-6">
              {/* Quick Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700">
                    {subscriptionStats.resources?.condominiums || 0}
                  </div>
                  <div className="text-xs text-blue-600">Condomini</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">
                    {subscriptionStats.resources?.units || 0}
                  </div>
                  <div className="text-xs text-green-600">Unità</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-purple-700">
                    {subscriptionStats.resources?.people || 0}
                  </div>
                  <div className="text-xs text-purple-600">Anagrafiche</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">
                    {subscriptionStats.resources?.documents || 0}
                  </div>
                  <div className="text-xs text-amber-600">Documenti</div>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-indigo-700">
                    {subscriptionStats.resources?.storage_mb?.toFixed(1) || 0} MB
                  </div>
                  <div className="text-xs text-indigo-600">Storage</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-slate-700">
                    {subscriptionStats.resources?.users || 0}
                  </div>
                  <div className="text-xs text-slate-600">Utenti</div>
                </div>
              </div>

              {/* Workspace by Plan & Payment Stats */}
              <div className="grid md:grid-cols-3 gap-4">
                {/* Workspaces by Plan */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Workspace per Piano
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Trial</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-yellow-50">{subscriptionStats.workspaces_by_plan?.TRIAL?.count || 0}</Badge>
                        <span className="text-xs text-green-600">{subscriptionStats.workspaces_by_plan?.TRIAL?.active || 0} attivi</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Basic</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-blue-50">{subscriptionStats.workspaces_by_plan?.BASIC?.count || 0}</Badge>
                        <span className="text-xs text-green-600">{subscriptionStats.workspaces_by_plan?.BASIC?.active || 0} attivi</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Pro</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-indigo-50">{subscriptionStats.workspaces_by_plan?.PRO?.count || 0}</Badge>
                        <span className="text-xs text-green-600">{subscriptionStats.workspaces_by_plan?.PRO?.active || 0} attivi</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payments */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Pagamenti
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Totale incassato</span>
                      <span className="font-semibold text-green-600">
                        €{((subscriptionStats.payments?.total_revenue_cents || 0) / 100).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">In attesa</span>
                      <span className="font-semibold text-amber-600">
                        €{((subscriptionStats.payments?.pending_revenue_cents || 0) / 100).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Carta: {subscriptionStats.payments?.by_provider?.CARD || 0}</span>
                      <span>Bonifico: {subscriptionStats.payments?.by_provider?.BANK || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Invoices */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Fatturazione
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Fatture emesse</span>
                      <Badge className="bg-green-100 text-green-800">
                        {subscriptionStats.invoices?.invoiced_payments || 0}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Da fatturare</span>
                      <Badge className="bg-amber-100 text-amber-800">
                        {subscriptionStats.invoices?.not_invoiced_payments || 0}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      {subscriptionStats.invoices?.by_status?.DRAFT || 0} bozze, {' '}
                      {subscriptionStats.invoices?.by_status?.SENT || 0} inviate
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Payments Table */}
              {subscriptionStats.recent_payments?.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Ultimi Pagamenti</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Order ID</th>
                          <th className="text-left py-2">Importo</th>
                          <th className="text-left py-2">Metodo</th>
                          <th className="text-left py-2">Stato</th>
                          <th className="text-left py-2">Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptionStats.recent_payments.slice(0, 5).map((pay, idx) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="py-2 font-mono text-xs">{pay.order_id}</td>
                            <td className="py-2">€{((pay.amount_cents || 0) / 100).toFixed(2)}</td>
                            <td className="py-2">
                              <Badge variant="outline" className="text-xs">
                                {pay.provider === 'BANK' ? 'Bonifico' : 'Carta'}
                              </Badge>
                            </td>
                            <td className="py-2">
                              <Badge className={
                                pay.status === 'PAID' ? 'bg-green-100 text-green-800' :
                                pay.status === 'PENDING' ? 'bg-amber-100 text-amber-800' :
                                'bg-red-100 text-red-800'
                              }>
                                {pay.status}
                              </Badge>
                            </td>
                            <td className="py-2 text-xs text-muted-foreground">
                              {pay.created_at ? new Date(pay.created_at).toLocaleDateString('it-IT') : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              Caricamento statistiche...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Condominiums Report */}
      <ClientCondominiumsReport />

      {/* Dev Tools Section - Only visible in dev mode */}
      {devConfig?.is_dev_mode && (
        <Card className="border-orange-200 bg-orange-50/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Terminal className="h-5 w-5 text-orange-600" />
              <CardTitle className="text-orange-900">Dev Tools</CardTitle>
            </div>
            <CardDescription>
              <Alert variant="warning" className="mt-2 bg-orange-100 border-orange-300">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <AlertTitle className="text-orange-800">Solo ambiente di sviluppo</AlertTitle>
                <AlertDescription className="text-orange-700">
                  Questi strumenti sono disponibili solo in ambiente di sviluppo. Non usare in produzione.
                </AlertDescription>
              </Alert>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4 items-start">
              <Button 
                onClick={handleRunSeed}
                disabled={seedLoading}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {seedLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Esecuzione...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Esegui Seed Demo
                  </>
                )}
              </Button>
              <p className="text-sm text-muted-foreground">
                Crea o riutilizza workspace demo, utenti e dati di test.
                L'operazione è idempotente (non crea duplicati).
              </p>
            </div>

            {/* Seed Result */}
            {seedResult && (
              <div className="mt-4 p-4 bg-white rounded-lg border space-y-4">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Seed completato!</span>
                </div>

                {/* Notes */}
                {seedResult.notes?.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {seedResult.notes.map((note, i) => (
                      <span key={i} className="inline-block mr-2 px-2 py-0.5 bg-slate-100 rounded text-xs">
                        {note}
                      </span>
                    ))}
                  </div>
                )}

                {/* Credentials Grid */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Super Admin */}
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-medium text-purple-900 text-sm mb-2">🔐 Super Admin</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Email:</span>
                        <div className="flex items-center gap-1">
                          <code className="bg-white px-2 py-0.5 rounded">{seedResult.super_admin?.email}</code>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard(seedResult.super_admin?.email, 'Email')}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Password:</span>
                        <div className="flex items-center gap-1">
                          <code className="bg-white px-2 py-0.5 rounded">{seedResult.super_admin?.password}</code>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard(seedResult.super_admin?.password, 'Password')}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Demo Admin */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-900 text-sm mb-2">👤 Demo Admin</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Email:</span>
                        <div className="flex items-center gap-1">
                          <code className="bg-white px-2 py-0.5 rounded">{seedResult.demo_admin?.email}</code>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard(seedResult.demo_admin?.email, 'Email')}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Password temp:</span>
                        <div className="flex items-center gap-1">
                          <code className="bg-white px-2 py-0.5 rounded">{seedResult.demo_admin?.temp_password}</code>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard(seedResult.demo_admin?.temp_password, 'Password')}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {seedResult.demo_admin?.force_password_reset && (
                        <Badge className="bg-yellow-100 text-yellow-800 text-xs">Cambio password al primo login</Badge>
                      )}
                    </div>
                  </div>

                  {/* Workspace */}
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-medium text-green-900 text-sm mb-2">🏢 Workspace</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-muted-foreground">Nome:</span> {seedResult.workspace?.name}</p>
                      <p><span className="text-muted-foreground">Piano:</span> 
                        <Badge className="ml-2 bg-yellow-100 text-yellow-800">{seedResult.workspace?.plan_status}</Badge>
                      </p>
                      <p><span className="text-muted-foreground">Scadenza trial:</span> {formatDate(seedResult.workspace?.trial_ends_at)}</p>
                    </div>
                  </div>

                  {/* Invite Links */}
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="font-medium text-amber-900 text-sm mb-2">✉️ Invito Demo</h4>
                    <div className="space-y-2 text-sm">
                      <p className="text-muted-foreground">{seedResult.invite?.person_name}</p>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(seedResult.invite?.invite_link, 'Link invito')}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Copia Link
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="bg-green-50 text-green-700 hover:bg-green-100"
                          onClick={() => window.open(seedResult.invite?.whatsapp_link, '_blank')}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          WhatsApp
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Workspaces Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Gestione Workspace</CardTitle>
              <CardDescription>Gestisci tutti i tenant della piattaforma</CardDescription>
            </div>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Nuovo Workspace
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crea Nuovo Workspace</DialogTitle>
                  <DialogDescription>
                    Crea un nuovo workspace con admin
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nome Workspace *</Label>
                    <Input
                      value={createForm.name}
                      onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                      placeholder="es. Studio Rossi"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Admin *</Label>
                    <Input
                      type="email"
                      value={createForm.admin_email}
                      onChange={(e) => setCreateForm({...createForm, admin_email: e.target.value})}
                      placeholder="admin@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nome Admin *</Label>
                    <Input
                      value={createForm.admin_full_name}
                      onChange={(e) => setCreateForm({...createForm, admin_full_name: e.target.value})}
                      placeholder="Mario Rossi"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Annulla
                  </Button>
                  <Button onClick={handleCreateWorkspace}>
                    Crea Workspace
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <Input
                placeholder="Cerca per nome o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Tutti gli stati" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti</SelectItem>
                <SelectItem value="TRIAL">Trial</SelectItem>
                <SelectItem value="ACTIVE">Attivo</SelectItem>
                <SelectItem value="EXPIRED">Scaduto</SelectItem>
                <SelectItem value="SUSPENDED">Sospeso</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { setSearchTerm(''); setStatusFilter(''); }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Workspace</TableHead>
                <TableHead>Admin</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Scadenza Trial</TableHead>
                <TableHead>Creato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workspaces.map((ws) => {
                const statusInfo = PLAN_STATUS[ws.plan_status] || PLAN_STATUS.TRIAL;
                const StatusIcon = statusInfo.icon;
                
                return (
                  <TableRow key={ws.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{ws.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {ws.stats?.condominiums || 0} condomini
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p>{ws.admin_name || '-'}</p>
                        <p className="text-xs text-muted-foreground">{ws.admin_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusInfo.color}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {statusInfo.label}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(ws.trial_ends_at)}</TableCell>
                    <TableCell>{formatDate(ws.created_at)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => fetchWorkspaceDetail(ws.id)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {ws.plan_status !== 'ACTIVE' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleActivate(ws.id)}
                          title="Attiva"
                        >
                          <Play className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                      {ws.plan_status !== 'SUSPENDED' && ws.plan_status !== 'EXPIRED' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedWorkspace(ws); setIsSuspendOpen(true); }}
                          title="Sospendi"
                        >
                          <Pause className="h-4 w-4 text-orange-600" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {workspaces.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Nessun workspace trovato
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Workspace Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {selectedWorkspace?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedWorkspace && (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList>
                <TabsTrigger value="overview">Panoramica</TabsTrigger>
                <TabsTrigger value="plan">Piano e Limiti</TabsTrigger>
                <TabsTrigger value="billing">Fatturazione</TabsTrigger>
                <TabsTrigger value="payments">Pagamenti</TabsTrigger>
                <TabsTrigger value="users">Utenti</TabsTrigger>
              </TabsList>
              
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Stato Piano</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2">
                        <Badge className={PLAN_STATUS[selectedWorkspace.plan_status]?.color || ''}>
                          {PLAN_STATUS[selectedWorkspace.plan_status]?.label}
                        </Badge>
                        <Badge variant="outline">{selectedWorkspace.plan_code || 'TRIAL'}</Badge>
                      </div>
                      {selectedWorkspace.lock_reason && (
                        <p className="text-sm text-red-600 mt-2">{selectedWorkspace.lock_reason}</p>
                      )}
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Date</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <p>Trial iniziata: {formatDate(selectedWorkspace.trial_started_at)}</p>
                      <p>Trial scade: {formatDate(selectedWorkspace.trial_ends_at)}</p>
                      {selectedWorkspace.subscription_started_at && (
                        <p className="text-green-600">Abbonamento dal: {formatDate(selectedWorkspace.subscription_started_at)}</p>
                      )}
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Statistiche</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <p>Condomini: {selectedWorkspace.stats?.condominiums || 0} / {selectedWorkspace.max_condominiums}</p>
                      <p>Unità: {selectedWorkspace.stats?.units || 0} / {selectedWorkspace.max_units}</p>
                      <p>Persone: {selectedWorkspace.stats?.people || 0}</p>
                      <p>Documenti: {selectedWorkspace.stats?.documents || 0}</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Admin</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm">
                      <p className="font-medium">{selectedWorkspace.admin?.full_name}</p>
                      <p className="text-muted-foreground">{selectedWorkspace.admin?.email}</p>
                      {selectedWorkspace.admin?.force_password_change && (
                        <Badge className="mt-2 bg-orange-100 text-orange-800">Cambio password richiesto</Badge>
                      )}
                    </CardContent>
                  </Card>
                </div>
                
                {/* Actions */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Azioni</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-2">
                    {selectedWorkspace.plan_status !== 'ACTIVE' && (
                      <>
                        <Button size="sm" onClick={() => handleActivate(selectedWorkspace.id)}>
                          <Play className="h-4 w-4 mr-1" />
                          Attiva
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleActivateReconfirm(selectedWorkspace.id)}>
                          <Key className="h-4 w-4 mr-1" />
                          Attiva + Reset Password
                        </Button>
                      </>
                    )}
                    
                    {selectedWorkspace.plan_status === 'TRIAL' && (
                      <Button size="sm" variant="outline" onClick={() => setIsExtendOpen(true)}>
                        <Calendar className="h-4 w-4 mr-1" />
                        Estendi Trial
                      </Button>
                    )}
                    
                    {selectedWorkspace.plan_status !== 'SUSPENDED' && (
                      <Button size="sm" variant="outline" className="text-orange-600" onClick={() => setIsSuspendOpen(true)}>
                        <Pause className="h-4 w-4 mr-1" />
                        Sospendi
                      </Button>
                    )}
                    
                    {selectedWorkspace.plan_status !== 'EXPIRED' && (
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleExpire(selectedWorkspace.id)}>
                        <XCircle className="h-4 w-4 mr-1" />
                        Forza Scadenza
                      </Button>
                    )}
                    
                    {selectedWorkspace.admin && (
                      <Button size="sm" variant="outline" onClick={() => handleForcePasswordReset(selectedWorkspace.admin.id)}>
                        <Mail className="h-4 w-4 mr-1" />
                        Reset Password Admin
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Plan & Limits Tab */}
              <TabsContent value="plan" className="space-y-4">
                <PlanManagement 
                  workspace={selectedWorkspace} 
                  token={token}
                  onUpdate={() => fetchWorkspaceDetail(selectedWorkspace.id)}
                />
              </TabsContent>
              
              {/* Billing Tab */}
              <TabsContent value="billing" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-sm">Dati Fatturazione</CardTitle>
                      <Button size="sm" variant="outline" onClick={() => setIsBillingOpen(true)}>
                        Modifica
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {selectedWorkspace.billing_customer ? (
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <Label className="text-muted-foreground">Ragione Sociale</Label>
                          <p>{selectedWorkspace.billing_customer.legal_name || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">P.IVA</Label>
                          <p>{selectedWorkspace.billing_customer.vat_number || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Codice Fiscale</Label>
                          <p>{selectedWorkspace.billing_customer.fiscal_code || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Email</Label>
                          <p>{selectedWorkspace.billing_customer.email || '-'}</p>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-muted-foreground">Indirizzo</Label>
                          <p>
                            {selectedWorkspace.billing_customer.address || '-'}
                            {selectedWorkspace.billing_customer.city && `, ${selectedWorkspace.billing_customer.city}`}
                            {selectedWorkspace.billing_customer.postal_code && ` ${selectedWorkspace.billing_customer.postal_code}`}
                          </p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">PEC</Label>
                          <p>{selectedWorkspace.billing_customer.email_pec || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Codice SDI</Label>
                          <p>{selectedWorkspace.billing_customer.sdi_code || '-'}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">Nessun dato di fatturazione</p>
                    )}
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Note</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{selectedWorkspace.billing_notes || 'Nessuna nota'}</p>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Payments Tab */}
              <TabsContent value="payments" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">Storico Pagamenti</h4>
                  <Button size="sm" onClick={() => setIsPaymentOpen(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Registra Pagamento
                  </Button>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Importo</TableHead>
                      <TableHead>Metodo</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Riferimento</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedWorkspace.payments || []).map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{formatDateTime(payment.created_at)}</TableCell>
                        <TableCell className="font-medium">€{payment.amount?.toFixed(2)}</TableCell>
                        <TableCell>{PAYMENT_METHOD[payment.method] || payment.method}</TableCell>
                        <TableCell>
                          <Badge className={PAYMENT_STATUS[payment.status]?.color || ''}>
                            {PAYMENT_STATUS[payment.status]?.label || payment.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{payment.reference || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {(!selectedWorkspace.payments || selectedWorkspace.payments.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          Nessun pagamento registrato
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TabsContent>
              
              {/* Users Tab */}
              <TabsContent value="users" className="space-y-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Creato</TableHead>
                      <TableHead>Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedWorkspace.users || []).map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {u.full_name}
                            {u.force_password_change && (
                              <Badge variant="outline" className="text-orange-600">Reset richiesto</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{formatDate(u.created_at)}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleForcePasswordReset(u.id)}
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Extend Trial Dialog */}
      <Dialog open={isExtendOpen} onOpenChange={setIsExtendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Estendi Trial</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Giorni da aggiungere</Label>
              <Input
                type="number"
                min="1"
                max="90"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExtendOpen(false)}>Annulla</Button>
            <Button onClick={handleExtendTrial}>Estendi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={isSuspendOpen} onOpenChange={setIsSuspendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sospendi Workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Motivo della sospensione *</Label>
              <Textarea
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="es. Mancato pagamento"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSuspendOpen(false)}>Annulla</Button>
            <Button variant="destructive" onClick={handleSuspend}>Sospendi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registra Pagamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importo (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Metodo</Label>
                <Select value={paymentForm.method} onValueChange={(v) => setPaymentForm({...paymentForm, method: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BANK_TRANSFER">Bonifico</SelectItem>
                    <SelectItem value="CARD">Carta</SelectItem>
                    <SelectItem value="CASH">Contanti</SelectItem>
                    <SelectItem value="OTHER">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Riferimento</Label>
              <Input
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm({...paymentForm, reference: e.target.value})}
                placeholder="es. TRN123456789"
              />
            </div>
            <div className="space-y-2">
              <Label>Stato</Label>
              <Select value={paymentForm.status} onValueChange={(v) => setPaymentForm({...paymentForm, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PAID">Pagato</SelectItem>
                  <SelectItem value="PENDING">In attesa</SelectItem>
                  <SelectItem value="FAILED">Fallito</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPaymentOpen(false)}>Annulla</Button>
            <Button onClick={handleRecordPayment}>Registra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Billing Dialog */}
      <Dialog open={isBillingOpen} onOpenChange={setIsBillingOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica Dati Fatturazione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Ragione Sociale</Label>
                <Input
                  value={billingForm.legal_name || ''}
                  onChange={(e) => setBillingForm({...billingForm, legal_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Partita IVA</Label>
                <Input
                  value={billingForm.vat_number || ''}
                  onChange={(e) => setBillingForm({...billingForm, vat_number: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Codice Fiscale</Label>
                <Input
                  value={billingForm.fiscal_code || ''}
                  onChange={(e) => setBillingForm({...billingForm, fiscal_code: e.target.value})}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Indirizzo</Label>
                <Input
                  value={billingForm.address || ''}
                  onChange={(e) => setBillingForm({...billingForm, address: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Città</Label>
                <Input
                  value={billingForm.city || ''}
                  onChange={(e) => setBillingForm({...billingForm, city: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>CAP</Label>
                <Input
                  value={billingForm.postal_code || ''}
                  onChange={(e) => setBillingForm({...billingForm, postal_code: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={billingForm.email || ''}
                  onChange={(e) => setBillingForm({...billingForm, email: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>PEC</Label>
                <Input
                  value={billingForm.email_pec || ''}
                  onChange={(e) => setBillingForm({...billingForm, email_pec: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Codice SDI</Label>
                <Input
                  value={billingForm.sdi_code || ''}
                  onChange={(e) => setBillingForm({...billingForm, sdi_code: e.target.value})}
                  maxLength={7}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBillingOpen(false)}>Annulla</Button>
            <Button onClick={handleUpdateBilling}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Plan Management Component (for Super Admin)
function PlanManagement({ workspace, token, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [planCode, setPlanCode] = useState(workspace?.plan_code || 'TRIAL');
  const [softLimits, setSoftLimits] = useState(workspace?.soft_limits ?? true);
  const [limits, setLimits] = useState({
    max_condominiums: workspace?.limits?.max_condominiums || workspace?.max_condominiums || 5,
    max_units: workspace?.limits?.max_units || workspace?.max_units || 100,
    max_users: workspace?.limits?.max_users || 20,
    max_documents: workspace?.limits?.max_documents || 500,
    max_storage_mb: workspace?.limits?.max_storage_mb || 1024
  });

  useEffect(() => {
    if (workspace) {
      fetchUsage();
    }
  }, [workspace]);

  const fetchUsage = async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/workspaces/${workspace.id}/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsageData(data);
      }
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const handleSavePlan = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/superadmin/workspaces/${workspace.id}/plan`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_code: planCode,
          soft_limits: softLimits,
          max_condominiums: limits.max_condominiums,
          max_units: limits.max_units,
          max_users: limits.max_users,
          max_documents: limits.max_documents,
          max_storage_mb: limits.max_storage_mb
        })
      });
      
      if (res.ok) {
        toast.success('Piano aggiornato');
        onUpdate();
        fetchUsage();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore nella richiesta');
    } finally {
      setLoading(false);
    }
  };

  const PLANS = [
    { code: 'TRIAL', name: 'Trial', limits: { max_condominiums: 3, max_units: 50, max_users: 20, max_documents: 500, max_storage_mb: 1024 } },
    { code: 'BASIC', name: 'Basic', limits: { max_condominiums: 5, max_units: 200, max_users: 50, max_documents: 5000, max_storage_mb: 10240 } },
    { code: 'PRO', name: 'Pro', limits: { max_condominiums: 50, max_units: 5000, max_users: 500, max_documents: 50000, max_storage_mb: 102400 } }
  ];

  const handlePlanChange = (code) => {
    setPlanCode(code);
    const plan = PLANS.find(p => p.code === code);
    if (plan) {
      setLimits(plan.limits);
      setSoftLimits(code === 'TRIAL');
    }
  };

  const UsageBar = ({ label, current, limit }) => {
    const percent = limit > 0 ? (current / limit * 100) : 0;
    const warning = percent >= 80;
    const exceeded = percent >= 100;
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span>{label}</span>
          <span className={exceeded ? 'text-red-600 font-medium' : warning ? 'text-orange-600' : ''}>
            {current} / {limit} ({percent.toFixed(0)}%)
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all ${exceeded ? 'bg-red-500' : warning ? 'bg-orange-500' : 'bg-blue-500'}`}
            style={{ width: `${Math.min(percent, 100)}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Current Usage */}
      {usageData && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-sm">Utilizzo Attuale</CardTitle>
              <Button size="sm" variant="ghost" onClick={fetchUsage}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <UsageBar 
              label="Condomini" 
              current={usageData.usage?.condominiums_count || 0} 
              limit={usageData.limits?.max_condominiums || limits.max_condominiums} 
            />
            <UsageBar 
              label="Unità" 
              current={usageData.usage?.units_count || 0} 
              limit={usageData.limits?.max_units || limits.max_units} 
            />
            <UsageBar 
              label="Utenti" 
              current={usageData.usage?.users_count || 0} 
              limit={usageData.limits?.max_users || limits.max_users} 
            />
            <UsageBar 
              label="Documenti" 
              current={usageData.usage?.documents_count || 0} 
              limit={usageData.limits?.max_documents || limits.max_documents} 
            />
            <UsageBar 
              label="Storage (MB)" 
              current={Math.round(usageData.usage?.storage_used_mb || 0)} 
              limit={usageData.limits?.max_storage_mb || limits.max_storage_mb} 
            />
          </CardContent>
        </Card>
      )}

      {/* Plan Selection */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Configurazione Piano</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Piano</Label>
            <Select value={planCode} onValueChange={handlePlanChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TRIAL">Trial (Gratuito)</SelectItem>
                <SelectItem value="BASIC">Basic (€49/mese)</SelectItem>
                <SelectItem value="PRO">Pro (€149/mese)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <input 
              type="checkbox" 
              id="soft-limits"
              checked={softLimits}
              onChange={(e) => setSoftLimits(e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="soft-limits" className="text-sm cursor-pointer">
              Limiti soft (solo avviso, nessun blocco)
            </Label>
          </div>

          <div className="border-t pt-4 space-y-3">
            <p className="text-sm font-medium">Limiti personalizzati</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Max Condomini</Label>
                <Input 
                  type="number" 
                  value={limits.max_condominiums}
                  onChange={(e) => setLimits({...limits, max_condominiums: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Max Unità</Label>
                <Input 
                  type="number" 
                  value={limits.max_units}
                  onChange={(e) => setLimits({...limits, max_units: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Max Utenti</Label>
                <Input 
                  type="number" 
                  value={limits.max_users}
                  onChange={(e) => setLimits({...limits, max_users: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Max Documenti</Label>
                <Input 
                  type="number" 
                  value={limits.max_documents}
                  onChange={(e) => setLimits({...limits, max_documents: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Max Storage (MB)</Label>
                <Input 
                  type="number" 
                  value={limits.max_storage_mb}
                  onChange={(e) => setLimits({...limits, max_storage_mb: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
          </div>

          <Button onClick={handleSavePlan} disabled={loading} className="w-full">
            {loading ? 'Salvando...' : 'Salva Configurazione Piano'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default SuperAdmin;
